import { useState, useEffect } from "react";
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Clock, User, BookOpen, Edit, Trash2 } from "lucide-react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import AttendanceTracker from "@/components/attendance/AttendanceTracker";

interface TutoringSchedule {
  id: string;
  student_email: string;
  student_name: string | null;
  session_date: string;
  start_time: string;
  end_time: string;
  subject: string;
  topic: string | null;
  notes: string | null;
  status: string;
}

interface StudentListSchedule {
  id: string;
  list_id: string;
  session_date: string;
  start_time: string;
  end_time: string;
  subject: string;
  topic: string | null;
  notes: string | null;
  instructor_student_lists: {
    list_name: string;
  };
}

interface TutoringCalendarProps {
  instructorId: string;
  isInstructor?: boolean;
}

export default function TutoringCalendar({ instructorId, isInstructor = false }: TutoringCalendarProps) {
  const [schedules, setSchedules] = useState<TutoringSchedule[]>([]);
  const [listSchedules, setListSchedules] = useState<StudentListSchedule[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<TutoringSchedule | StudentListSchedule | null>(null);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [showAttendanceDialog, setShowAttendanceDialog] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSchedules();
  }, [instructorId]);

  const loadSchedules = async () => {
    try {
      // Load individual tutoring schedules
      let query = supabase
        .from('tutoring_schedules')
        .select('*')
        .order('session_date', { ascending: true })
        .order('start_time', { ascending: true });

      if (isInstructor) {
        query = query.eq('instructor_id', instructorId);
      } else {
        query = query.eq('student_email', instructorId);
      }

      const { data, error } = await query;
      if (error) throw error;
      setSchedules(data || []);

      // Load student list schedules (bulk schedules)
      if (isInstructor) {
        const { data: listData, error: listError } = await supabase
          .from('student_list_schedules')
          .select(`
            *,
            instructor_student_lists(list_name)
          `)
          .eq('instructor_id', instructorId)
          .order('session_date', { ascending: true })
          .order('start_time', { ascending: true });

        if (listError) throw listError;
        setListSchedules(listData || []);
      }
    } catch (error) {
      console.error('Error loading schedules:', error);
      toast.error('Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled': return 'default';
      case 'completed': return 'secondary';
      case 'cancelled': return 'destructive';
      case 'rescheduled': return 'outline';
      default: return 'default';
    }
  };

  const deleteSchedule = async (scheduleId: string, isListSchedule: boolean = false) => {
    if (!isInstructor) return;
    
    try {
      const table = isListSchedule ? 'student_list_schedules' : 'tutoring_schedules';
      const { error } = await supabase
        .from(table)
        .delete()
        .eq('id', scheduleId);

      if (error) throw error;

      if (isListSchedule) {
        setListSchedules(listSchedules.filter(s => s.id !== scheduleId));
      } else {
        setSchedules(schedules.filter(s => s.id !== scheduleId));
      }
      
      setShowEventDialog(false);
      toast.success('Schedule deleted successfully');
      loadSchedules();
    } catch (error) {
      console.error('Error deleting schedule:', error);
      toast.error('Failed to delete schedule');
    }
  };

  // Convert schedules to FullCalendar events
  const calendarEvents = [
    // Individual schedules
    ...schedules.map(schedule => ({
      id: schedule.id,
      title: isInstructor 
        ? (schedule.student_name || schedule.student_email)
        : schedule.subject,
      start: `${schedule.session_date}T${schedule.start_time}`,
      end: `${schedule.session_date}T${schedule.end_time}`,
      backgroundColor: schedule.status === 'completed' ? '#22c55e' : 
                      schedule.status === 'cancelled' ? '#ef4444' : 
                      schedule.status === 'rescheduled' ? '#f59e0b' : '#3b82f6',
      borderColor: 'transparent',
      extendedProps: {
        schedule: schedule,
        isListSchedule: false
      }
    })),
    // Student list schedules
    ...listSchedules.map(schedule => ({
      id: `list-${schedule.id}`,
      title: `📋 ${schedule.instructor_student_lists.list_name}`,
      start: `${schedule.session_date}T${schedule.start_time}`,
      end: `${schedule.session_date}T${schedule.end_time}`,
      backgroundColor: '#8b5cf6',
      borderColor: 'transparent',
      extendedProps: {
        schedule: schedule,
        isListSchedule: true
      }
    }))
  ];

  const handleEventClick = (clickInfo: any) => {
    const schedule = clickInfo.event.extendedProps.schedule;
    setSelectedEvent(schedule);
    setShowEventDialog(true);
  };

  const handleDateSelect = (selectInfo: any) => {
    if (isInstructor) {
      const selectedDate = selectInfo.start.toISOString().split('T')[0];
      window.location.href = `/instructor/tutoring/create?date=${selectedDate}`;
    }
  };

  const isListSchedule = (event: any): event is StudentListSchedule => {
    return 'list_id' in event;
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>
            {isInstructor && 'My Tutoring Schedule'}
            {!isInstructor && 'My Tutoring Sessions'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p>Loading calendar...</p>
            </div>
          ) : (
            <div className="calendar-container">
              <FullCalendar
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                headerToolbar={{
                  left: 'prev,next today',
                  center: 'title',
                  right: 'dayGridMonth,timeGridWeek,timeGridDay'
                }}
                events={calendarEvents}
                eventClick={handleEventClick}
                selectable={isInstructor}
                select={handleDateSelect}
                height="auto"
                aspectRatio={1.8}
                dayMaxEvents={3}
                moreLinkClick="popover"
                eventDisplay="block"
                displayEventTime={true}
                eventTimeFormat={{
                  hour: 'numeric',
                  minute: '2-digit',
                  meridiem: 'short'
                }}
                slotLabelFormat={{
                  hour: 'numeric',
                  minute: '2-digit',
                  meridiem: 'short'
                }}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Event Details Dialog */}
      <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Session Details</DialogTitle>
          </DialogHeader>
          
          {selectedEvent && (
            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-lg">
                  {isListSchedule(selectedEvent) 
                    ? `📋 ${selectedEvent.instructor_student_lists.list_name}`
                    : isInstructor 
                      ? (selectedEvent.student_name || selectedEvent.student_email)
                      : selectedEvent.subject
                  }
                </h3>
                {!isListSchedule(selectedEvent) && (
                  <Badge variant={getStatusColor(selectedEvent.status)} className="mt-1">
                    {selectedEvent.status.charAt(0).toUpperCase() + selectedEvent.status.slice(1)}
                  </Badge>
                )}
              </div>

              <div className="grid grid-cols-1 gap-3">
                <div className="flex items-center gap-2">
                  <BookOpen className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{selectedEvent.subject}</span>
                </div>

                {selectedEvent.topic && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Topic: {selectedEvent.topic}</span>
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">
                    {format(new Date(selectedEvent.session_date), 'MMM dd, yyyy')} • {' '}
                    {formatTime(selectedEvent.start_time)} - {formatTime(selectedEvent.end_time)}
                  </span>
                </div>

                {selectedEvent.notes && (
                  <div className="mt-3">
                    <p className="text-sm font-medium mb-1">Notes:</p>
                    <p className="text-sm text-muted-foreground">{selectedEvent.notes}</p>
                  </div>
                )}
              </div>

              {isInstructor && (
                <div className="flex gap-2 pt-4">
                  {!isListSchedule(selectedEvent) && (
                    <>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => {
                          setShowEventDialog(false);
                          setShowAttendanceDialog(true);
                        }}
                        className="flex-1"
                      >
                        Mark Attendance
                      </Button>
                      <Button variant="outline" size="sm" asChild className="flex-1">
                        <Link to={`/instructor/tutoring/edit/${selectedEvent.id}`}>
                          <Edit className="h-4 w-4 mr-2" />
                          Edit
                        </Link>
                      </Button>
                    </>
                  )}
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => deleteSchedule(selectedEvent.id, isListSchedule(selectedEvent))}
                    className="flex-1"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Attendance Tracking Dialog */}
      {selectedEvent && !isListSchedule(selectedEvent) && (
        <Dialog open={showAttendanceDialog} onOpenChange={setShowAttendanceDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <AttendanceTracker
              scheduleId={selectedEvent.id}
              sessionDate={selectedEvent.session_date}
              subject={selectedEvent.subject}
              onClose={() => {
                setShowAttendanceDialog(false);
                loadSchedules();
              }}
            />
          </DialogContent>
        </Dialog>
      )}

      <style>{`
        .calendar-container .fc {
          font-family: inherit;
        }
        
        .calendar-container .fc-toolbar-title {
          font-size: 1.125rem;
          font-weight: 600;
        }
        
        .calendar-container .fc-button {
          background: hsl(var(--primary));
          border-color: hsl(var(--primary));
          font-size: 0.875rem;
        }
        
        .calendar-container .fc-button:hover:not(:disabled) {
          background: hsl(var(--primary)) !important;
          border-color: hsl(var(--primary)) !important;
          opacity: 0.9;
        }
        
        .calendar-container .fc-button:focus {
          box-shadow: 0 0 0 0.2rem hsl(var(--primary) / 0.25);
        }
        
        .calendar-container .fc-event {
          border-radius: 4px;
          padding: 2px 4px;
          font-size: 0.75rem;
          cursor: pointer;
        }
        
        .calendar-container .fc-day-today {
          background-color: hsl(var(--primary) / 0.05) !important;
        }
        
        .calendar-container .fc-daygrid-event {
          margin-bottom: 1px;
        }
      `}</style>
    </>
  );
}